import { getConditionalObject } from 'utils/data';

import { STATUS_COLUMN_FRACTION, STATUS_COLUMN_WIDTH } from '../Alerts.constants';
import { checklistListStyles, truncatedNameStyles } from '../Alerts.styles';

import { getCalc } from './ChecklistItem.utils';

const BASE_PADDING_PX = 12;
const NESTING_LEVEL_WIDTH_PX = 50;

export const getChecklistItemStyles = ({ isPendingChange, isManuallyChanged }) => ({
  borderRadius: ({ shape }) => shape.radius.s,
  border: ({ palette }) =>
    `1px solid ${
      isPendingChange && isManuallyChanged ? palette.text.tableHighlight : palette.border.default
    }`,
  backgroundColor: ({ palette }) => palette.background.secondary,
  height: '54px',
  paddingLeft: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flex: '1',
  position: 'relative',
});

export const checklistInfoStyles = {
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  left: 0,
  top: '4px',
  bottom: '4px',
  width: '100%',
  marginLeft: '0 !important',
  justifyContent: 'flex-end',

  '.checklistItemValue': {
    color: ({ palette }) => palette.text.primary,
  },
};

// * differenceWithRootCategory - difference between width of category panel and width of checkitem panel
// * columnPercentWidth - width of checkitem panel in percentage relative to width of category panel
// * all this values obtained from design
export const getChecklistStatusContainerStyles = ({ nestedLevel, isEmpty }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  gap: '8px',
  ...getConditionalObject(nestedLevel === 0, {
    width: STATUS_COLUMN_WIDTH,
  }),
  ...getConditionalObject(nestedLevel > 0, () => ({
    width: getCalc({
      differenceWithRootCategory: isEmpty
        ? BASE_PADDING_PX + (nestedLevel - 1) * NESTING_LEVEL_WIDTH_PX
        : nestedLevel * NESTING_LEVEL_WIDTH_PX,
      columnPercentWidth: STATUS_COLUMN_FRACTION,
    }),
  })),
});

export const getChecklistActionInfoContainerStyles = ({ nestedLevel, isEmpty }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '0 16px',
  ...getConditionalObject(nestedLevel === 0, {
    width: '14%',
  }),
  ...getConditionalObject(nestedLevel > 0, () => ({
    width: getCalc({
      differenceWithRootCategory: isEmpty
        ? BASE_PADDING_PX + (nestedLevel - 1) * NESTING_LEVEL_WIDTH_PX
        : nestedLevel * NESTING_LEVEL_WIDTH_PX,
      columnPercentWidth: 0.14,
    }),
  })),
});

export const getChecklistActionContainerStyles = ({ nestedLevel, isEmpty }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  ...getConditionalObject(nestedLevel === 0, {
    width: '22%',
  }),
  ...getConditionalObject(nestedLevel > 0, () => ({
    width: getCalc({
      differenceWithRootCategory: isEmpty
        ? BASE_PADDING_PX + (nestedLevel - 1) * NESTING_LEVEL_WIDTH_PX
        : nestedLevel * NESTING_LEVEL_WIDTH_PX,
      columnPercentWidth: 0.22,
    }),
  })),
});

export const connectorStyles = {
  width: '14px',
  height: '1px',
  borderTop: ({ palette }) => `1px solid ${palette.border.default}`,
  position: 'absolute',
  left: '-25px',
  top: '27px',
};

export const connectorCleanerStyles = {
  height: '100%',
  position: 'absolute',
  width: '2px',
  top: '27px',
  left: '-26px',
  backgroundColor: ({ palette }) => palette.background.paper,
};

export const getChecklistItemContainerStyles = ({ nestedLevel, isEmpty = false }) => ({
  display: 'flex',
  flex: '1',
  position: 'relative',
  ...getConditionalObject(nestedLevel === 0, () => ({
    marginRight: '12px',
    padding: '4px 0',
    ...getConditionalObject(isEmpty, {
      paddingLeft: '38px',
    }),
  })),
});

export const getAccordionItemCustomStyles = ({ isPendingChange, isManuallyChanged }) => ({
  boxShadow: 'unset',
  width: '100%',
  padding: '0 !important',
  '&.Mui-expanded': {
    margin: 0,
  },
  '&::before': {
    display: 'none',
  },
  '.MuiAccordionSummary-root': {
    flexDirection: 'row-reverse',
    '.MuiAccordionSummary-content.Mui-expanded': {
      margin: 0,
    },
  },
  '.MuiAccordionSummary-root.Mui-expanded': {
    minHeight: 'unset',
  },
  '.MuiAccordionSummary-expandIconWrapper': {
    marginRight: '4px',
    backgroundColor: ({ palette }) => palette.background.secondary,
    borderRadius: ({ shape }) => shape.radius.s,
    marginLeft: '16px',
    transform: 'rotate(-90deg)',
    '&.Mui-expanded': {
      transform: 'rotate(0deg)',
    },
    color: ({ palette }) => palette.text.secondary,
  },
  '.MuiAccordionDetails-root': {
    paddingBottom: 0,
  },
  '.MuiStack-root': {
    padding: 0,
  },
  '.accordion-summary-title': {
    ...truncatedNameStyles,
    fontSize: '14px',
    fontWeight: '400',
    color: ({ palette }) => palette.text.primary,
    lineHeight: '22px',
  },
  '& > .MuiAccordion-heading .accordion-summary-container': {
    height: '54px',
    borderRadius: ({ shape }) => shape.radius.s,
    border: ({ palette }) =>
      `1px solid ${
        isPendingChange && isManuallyChanged ? palette.text.tableHighlight : palette.border.default
      }`,
    padding: '8px 16px !important',
    backgroundColor: ({ palette }) => `${palette.background.secondary} !important`,
    marginRight: '0 !important',
    position: 'relative',
  },
});

export const checklistItemsListStyles = {
  ...checklistListStyles,
  paddingRight: 0,
};

export const statusIconContainerStyles = {
  display: 'flex',
  flexShrink: 0,
};

export const statusTextContainerStyles = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
};

export const valueContainerStyles = {
  display: 'flex',
  gap: '4px',
  maxWidth: '100%',
  alignItems: 'center',
};

export const clickableRowStyles = {
  cursor: 'pointer',
};
